import { FirebaseError } from 'firebase/app';
import {
  Auth,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  UserCredential,
} from 'firebase/auth';

/** Initiate email/password sign-up (non-blocking). */
export function initiateEmailSignUp(authInstance: Auth, email: string, password: string): Promise<UserCredential> {
  // CRITICAL: Call createUserWithEmailAndPassword directly. Do NOT use 'await createUserWithEmailAndPassword(...)'.
  return createUserWithEmailAndPassword(authInstance, email, password)
    .then(async (credential) => {
      await sendEmailVerification(credential.user);
      return credential;
    });
  // Code continues immediately. Auth state change is handled by onAuthStateChanged listener.
}

/** Initiate email/password sign-in (non-blocking). */
export function initiateEmailSignIn(authInstance: Auth, email: string, password: string): Promise<UserCredential> {
  // CRITICAL: Call signInWithEmailAndPassword directly. Do NOT use 'await signInWithEmailAndPassword(...)'.
  return signInWithEmailAndPassword(authInstance, email, password);
  // Code continues immediately. Auth state change is handled by onAuthStateChanged listener.
}

/** Initiate Google sign-in (non-blocking). Always use Popup to avoid redirect issues on custom domains. */
export function initiateGoogleSignIn(authInstance: Auth): Promise<UserCredential> {
  const provider = new GoogleAuthProvider();
  return signInWithPopup(authInstance, provider);
}

/** Initiate Apple (iOS) sign-in (non-blocking). Always use Popup to avoid redirect issues on custom domains. */
export function initiateAppleSignIn(authInstance: Auth): Promise<UserCredential> {
  const provider = new OAuthProvider('apple.com');
  return signInWithPopup(authInstance, provider);
}

/** Initiate password reset email (non-blocking). */
export function initiatePasswordReset(authInstance: Auth, email: string): Promise<void> {
  return sendPasswordResetEmail(authInstance, email);
}

/** Get localized translation key corresponding to Firebase Auth error code. */
export function getAuthErrorKey(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/invalid-credential':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-email':
        return 'login.invalidCredentials';
      case 'auth/email-already-in-use':
        return 'login.emailAlreadyInUse';
      case 'auth/weak-password':
        return 'login.weakPassword';
      case 'auth/too-many-requests':
        return 'login.tooManyRequests';
      case 'auth/network-request-failed':
        return 'login.networkError';
      case 'auth/popup-closed-by-user':
        return ''; // ignore to prevent displaying error on cancellation
      default:
        return 'login.genericError';
    }
  }
  return 'login.genericError';
}
